import { CreateLegalDocumentSchema, type CreateLegalDocumentRequest } from '@aysar/contracts';
import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { buildDocumentFields } from '../../config/legal-document-form.config';
import {
  useCreateDocumentMutation,
  useDocumentQuery,
  useUpdateDocumentMutation,
} from '../../services/legal/legal.api';

/**
 * Create and edit in one dialog. The create schema validates both: on edit the
 * key and type are disabled and prefilled, and only the fields the update
 * contract knows are sent — the contract has no `key` to refuse.
 */
export function LegalDocumentFormModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('legal');
  const toast = useToast();

  const documentId = typeof modalData?.['documentId'] === 'string' ? modalData['documentId'] : null;
  const isEditing = documentId !== null;

  const detail = useDocumentQuery(documentId ?? '', { skip: documentId === null });
  const [createDocument] = useCreateDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();

  return (
    <DynamicFormModal<CreateLegalDocumentRequest>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={
        isEditing
          ? t('document.form.editTitle', { key: detail.data?.key ?? '' })
          : t('document.form.createTitle')
      }
      description={t('document.form.description')}
      size="lg"
      isLoading={isEditing && detail.isLoading}
      fields={buildDocumentFields(t, isEditing)}
      schema={CreateLegalDocumentSchema}
      initialValues={
        isEditing
          ? {
              key: detail.data?.key,
              docType: detail.data?.docType,
              requiresConsent: detail.data?.requiresConsent,
              isActive: detail.data?.isActive,
            }
          : { requiresConsent: true, isActive: true }
      }
      onCancel={onCancel}
      onSubmit={async (values) => {
        try {
          if (isEditing) {
            await updateDocument({
              id: documentId,
              body: {
                requiresConsent: values.requiresConsent,
                isActive: values.isActive,
              },
            }).unwrap();
            toast.success(t('document.form.updated'));
          } else {
            await createDocument(values).unwrap();
            toast.success(t('document.form.created'));
          }
          onCancel();
        } catch (error) {
          throw toApiError(error);
        }
      }}
    />
  );
}
